import { useState } from "react";
import type { RecentlyCompletedGigsResponse } from "../../types/gigTypes";
import { formatRelativeAge } from "../../utils/DateUtils";
import { formatTeenGigCompensation } from "../../utils/GigCompensationUtils";
import { formatDistanceLabel } from "../../utils/LocationUtils";
import Button from "../ui/Button/Button";
import Modal from "../ui/Modal/Modal";

type RecentlyCompletedGigsModalProps = {
  data: RecentlyCompletedGigsResponse | undefined;
  isLoading: boolean;
};

const categoryToneClassNames = [
  "border-[#14B8A6]/35 bg-[#ECFDF5] text-[#065F46]",
  "border-[#F97316]/35 bg-[#FFF7ED] text-[#9A3412]",
  "border-myteengig-primary/25 bg-[#EAF8FF] text-myteengig-text-primary",
  "border-[#EAB308]/40 bg-[#FEFCE8] text-[#854D0E]",
];

const formatCompletedLocation = (gig: RecentlyCompletedGigsResponse["gigs"][number]) => {
  if (gig.remote) {
    return "Remote";
  }

  const cityState = [gig.city, gig.state].filter((part) => Boolean(part && part.trim())).join(", ");
  return cityState.length > 0 ? cityState : "Location not shared";
};

const RecentlyCompletedGigsModal = ({ data, isLoading }: RecentlyCompletedGigsModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const completedGigs = data?.gigs ?? [];
  const categories = data?.categories ?? [];
  const hasCompletedGigs = completedGigs.length > 0;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="inline-flex min-h-[44px] items-center justify-center gap-2 whitespace-nowrap"
        onClick={() => setIsOpen(true)}
        disabled={isLoading}
      >
        <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[#14B8A6]" />
        See Completed Opportunities!
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="See Completed Opportunities!"
        panelClassName="max-w-3xl"
      >
        <p className="text-sm leading-6 text-myteengig-text-muted">
          A look at the kind of work teens are finishing on MyTeenGig.
        </p>

        {!hasCompletedGigs ? (
          <div className="mt-4 rounded-lg border border-myteengig-border bg-myteengig-background px-4 py-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">No completed opportunities to show yet.</p>
          </div>
        ) : null}

        {hasCompletedGigs && categories.length > 0 ? (
          <div className="mt-4">
            <ul className="flex flex-wrap gap-2">
              {categories.map((category, index) => (
                <li
                  key={category.category}
                  className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs font-bold ${
                    categoryToneClassNames[index % categoryToneClassNames.length]
                  }`}
                >
                  {category.category}
                  <span className="rounded-sm bg-white/70 px-1.5 py-0.5 text-[11px] font-black">
                    {category.completedCount}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {hasCompletedGigs ? (
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {completedGigs.map((gig, index) => {
              const distanceLabel = formatDistanceLabel({ distanceMiles: gig.distanceMiles });

              return (
                <li
                  key={`${gig.category ?? "completed-gig"}-${gig.completedAt}-${index}`}
                  className="flex flex-col rounded-lg border border-myteengig-border bg-myteengig-background p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-bold text-myteengig-text-primary">{gig.category ?? "Completed gig"}</p>
                    <p className="shrink-0 rounded-md bg-white px-2 py-1 text-sm font-black text-myteengig-text-primary">
                      {formatTeenGigCompensation(gig.paymentAmountCents, gig.volunteer, "Paid")}
                    </p>
                  </div>

                  <dl className="mt-2 space-y-0.5 text-xs text-myteengig-text-muted">
                    <div className="flex gap-1">
                      <dt className="sr-only">Location</dt>
                      <dd>{formatCompletedLocation(gig)}</dd>
                      {distanceLabel ? <dd>{`· ${distanceLabel}`}</dd> : null}
                    </div>
                    <div>
                      <dt className="sr-only">Completed</dt>
                      <dd className="font-semibold text-myteengig-text-primary">
                        {`Completed ${formatRelativeAge(gig.completedAt).toLowerCase()}`}
                      </dd>
                    </div>
                  </dl>
                </li>
              );
            })}
          </ul>
        ) : null}

      </Modal>
    </>
  );
};

export default RecentlyCompletedGigsModal;
